import { AssetStatus } from '@mai-team-amusement/core';
import type { AssetView } from '@mai-fixed-asset/contracts';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Link as RouterLink } from 'react-router';
import { useAssetT } from '../i18n/index.js';
import { useLabels } from '../labels.js';

const STATUS_COLORS = {
  [AssetStatus.InUse]: 'success',
  [AssetStatus.InStorage]: 'default',
  [AssetStatus.UnderRepair]: 'warning',
  [AssetStatus.Lost]: 'error',
  [AssetStatus.Disposed]: 'default',
} as const satisfies Record<AssetStatus, 'success' | 'default' | 'warning' | 'error'>;

export function StatusChip({ status }: { status: AssetStatus }) {
  const labels = useLabels();
  return (
    <Chip
      size="small"
      label={labels.status(status)}
      color={STATUS_COLORS[status]}
      variant={status === AssetStatus.Disposed ? 'outlined' : 'filled'}
    />
  );
}

/** Assets with a link to each one; with `selection`, rows can be ticked (e.g. for labels). */
export function AssetTable({
  assets,
  selection,
}: {
  assets: readonly AssetView[];
  selection?: {
    selected: ReadonlySet<string>;
    toggle: (code: string) => void;
    /** Ticks or clears every row shown. */
    setAll: (codes: readonly string[], selected: boolean) => void;
  };
}) {
  const t = useAssetT();
  const labels = useLabels();
  const showAmounts = assets.some((asset) => asset.acquisition !== null);
  const ticked =
    selection === undefined
      ? 0
      : assets.filter((asset) => selection.selected.has(asset.code)).length;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {selection === undefined ? null : (
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={ticked === assets.length && assets.length > 0}
                  indeterminate={ticked > 0 && ticked < assets.length}
                  slotProps={{ input: { 'aria-label': t('assets.selectPage') } }}
                  onChange={(event) => {
                    selection.setAll(
                      assets.map((asset) => asset.code),
                      event.target.checked,
                    );
                  }}
                />
              </TableCell>
            )}
            <TableCell>{t('field.code')}</TableCell>
            <TableCell>{t('field.name')}</TableCell>
            <TableCell>{t('field.type')}</TableCell>
            <TableCell>{t('field.site')}</TableCell>
            <TableCell>{t('field.location')}</TableCell>
            <TableCell>{t('field.user')}</TableCell>
            <TableCell>{t('field.status')}</TableCell>
            {showAmounts ? <TableCell align="right">{t('field.amount')}</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {assets.map((asset) => (
            <TableRow key={asset.code} hover>
              {selection === undefined ? null : (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selection.selected.has(asset.code)}
                    slotProps={{ input: { 'aria-label': asset.code } }}
                    onChange={() => {
                      selection.toggle(asset.code);
                    }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                <Link component={RouterLink} to={`/assets/${asset.code}`}>
                  {asset.code}
                </Link>
              </TableCell>
              <TableCell>{asset.name}</TableCell>
              <TableCell>{asset.type.name}</TableCell>
              <TableCell>{asset.site.name ?? t('common.unknown')}</TableCell>
              <TableCell>{asset.location?.name ?? t('common.none')}</TableCell>
              <TableCell>{asset.user?.name ?? t('common.none')}</TableCell>
              <TableCell>
                <StatusChip status={asset.status} />
              </TableCell>
              {showAmounts ? (
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {asset.acquisition === null
                    ? t('common.none')
                    : labels.money(asset.acquisition.amount, asset.acquisition.currency)}
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
